import * as React from 'react';

/**
 * Tracks a map of indexed elements and scrolls the selected one
 * into view (horizontally) when it's not fully visible in the container.
 */
export const useScrollSelectedIntoView = (
  selectedIndex: number,
  containerRef: React.RefObject<HTMLElement>
) => {
  const itemRefs = React.useRef<Map<number, HTMLElement>>(new Map());

  React.useEffect(() => {
    const item = itemRefs.current.get(selectedIndex);
    const container = containerRef.current;
    if (!item || !container) return;

    const itemRect = item.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    if (
      itemRect.left < containerRect.left ||
      itemRect.right > containerRect.right
    ) {
      item.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [selectedIndex, containerRef]);

  const setItemRef = React.useCallback(
    (index: number) => (el: HTMLElement | null) => {
      if (el) {
        itemRefs.current.set(index, el);
      } else {
        itemRefs.current.delete(index);
      }
    },
    []
  );

  return { setItemRef };
};
